import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProperty } from '../context/PropertyContext';
import { useSwipe } from '../hooks/useSwipe';
import { usePermissions } from '../hooks/usePermissions';
import { Property } from '../types';
import { supabase } from '../integrations/supabase/client';
import { translateAmenity } from '../utils/translations';
import ImageModal from '../components/ImageModal';
import { 
  MapPin, 
  Bed, 
  Car, 
  Ruler, 
  Bath, 
  Heart, 
  Share2, 
  Phone,
  ArrowLeft,
  CheckCircle,
  ShoppingCart,
  Home,
  RefreshCw,
  Loader2,
  AlertTriangle
} from 'lucide-react';

const PropertyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getPropertyById, searchFilters } = useProperty();
  
  const { filterPropertyData } = usePermissions();
  
  // Estados de loading e dados
  const [property, setProperty] = useState<Property | null>(null);
  const [condominiumImages, setCondominiumImages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estados da UI
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isLiked, setIsLiked] = useState(false);

  // Função para buscar dados do condomínio se necessário
  const loadCondominiumData = async (condominiumId: string) => {
    try {
      const { data, error } = await supabase
        .from('condominiums')
        .select('images')
        .eq('id', condominiumId)
        .single();
      
      if (error) throw error;
      return data?.images || [];
    } catch (err) {
      console.error('Erro ao buscar dados do condomínio:', err);
      return [];
    }
  };

  // Funções de navegação de imagens
  const nextImage = () => {
    const allImages = getAllImages();
    setCurrentImageIndex((prev) => 
      prev === allImages.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    const allImages = getAllImages();
    setCurrentImageIndex((prev) => 
      prev === 0 ? allImages.length - 1 : prev - 1
    );
  };

  // Hook para swipe nas imagens
  const imageSwipeRef = useSwipe({
    onSwipeLeft: nextImage,
    onSwipeRight: prevImage
  });

  // Função para combinar todas as imagens (do imóvel + do condomínio)
  const getAllImages = () => {
    const propertyImages = property?.media?.images || [];
    const condImages = condominiumImages.map(img => ({
      ...img,
      isCondominium: true
    }));
    return [...propertyImages, ...condImages];
  };

  // Função para carregar dados do imóvel
  const loadProperty = useCallback(async () => {
    if (!id) {
      setError('ID do imóvel não fornecido');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      console.log('🔍 PropertyDetail - Buscando imóvel:', id);
      console.log('🔍 PropertyDetail - Timestamp:', new Date().toISOString());
      
      // Tentar buscar o imóvel localmente primeiro
      let foundProperty = getPropertyById(id);
      console.log('🔍 PropertyDetail - Resultado busca local:', foundProperty ? 'ENCONTRADO' : 'NÃO ENCONTRADO');
      
      // Se não encontrou localmente, buscar diretamente no banco
      if (!foundProperty) {
        console.log('💾 PropertyDetail - Buscando no banco de dados...');
        
        const { data, error } = await supabase
          .from('properties')
          .select(`
            *,
            broker_profile:profiles!properties_user_id_fkey (
              first_name,
              last_name,
              email,
              phone
            )
          `)
          .eq('listing_id', id)
          .eq('is_active', true)
          .maybeSingle();
        
        console.log('💾 PropertyDetail - Resultado da query:', { 
          data: data ? 'ENCONTRADO' : 'NÃO ENCONTRADO', 
          error: error?.message 
        });
        
        if (error) {
          console.error('❌ PropertyDetail - Erro ao buscar no banco:', error);
          setError('Erro ao buscar dados do imóvel');
          return;
        }
        
        if (data) {
          console.log('✅ PropertyDetail - Imóvel encontrado no banco:', {
            listing_id: data.listing_id,
            title: data.title,
            user_id: data.user_id,
            broker_profile: data.broker_profile
          });
          
          // Converter dados do Supabase para o formato Property
          foundProperty = {
            id: data.id,
            listingId: data.listing_id,
            propertyCode: data.listing_id,
            title: data.title,
            transactionType: data.transaction_type === 'sale' ? 'Venda' : 
                           data.transaction_type === 'rent' ? 'Aluguel' : 'Venda/Aluguel',
            publicationType: 'PREMIUM' as const,
            listDate: data.created_at,
            lastUpdateDate: data.updated_at,
            detailViewUrl: `/imovel/${data.listing_id}`,
            propertyType: data.property_type || 'Apartamento',
            description: data.description || '',
            listPrice: data.list_price || 0,
            rentalPrice: data.rental_price || 0,
            propertyAdministrationFee: data.condominium_fee || 0,
            yearlyTax: data.yearly_iptu || 0,
            livingArea: data.area_built || 0,
            bedrooms: data.bedrooms || 0,
            suites: data.suites || 0,
            bathrooms: data.bathrooms || 0,
            garage: {
              quantity: data.garage_quantity || 0,
              type: 'Covered'
            },
            features: Array.isArray(data.amenities) ? (data.amenities as string[]) : [],
            category: 'Médio' as const,
            olx: {
              sendToOlx: true,
              olxType: 'STANDARD' as const
            },
            location: {
              country: 'Brasil',
              state: data.state || 'SP',
              city: data.city || '',
              neighborhood: data.neighborhood || '',
              address: data.address || '',
              postalCode: data.zip_code || '',
              streetNumber: data.street_number || '',
              complement: data.complement || undefined,
              latitude: Number(data.latitude) || 0,
              longitude: Number(data.longitude) || 0
            },
            totalArea: data.total_area || undefined,
            media: {
              images: Array.isArray(data.images) ? (data.images as any[]).map(img => ({
                url: img.url || '',
                primary: img.primary || false,
                id: img.id,
                name: img.name
              })) : [],
              youtubeVideoUrl: data.video_url || undefined,
              virtualTourUrl: data.virtual_tour_url || undefined
            },
            owner: {
              name: data.owner_name || '',
              phone: data.owner_phone || '',
              email: data.owner_email || ''
            },
            contact: {
              brokerName: data.owner_name || '',
              brokerPhone: data.owner_phone || '',
              brokerEmail: data.owner_email || ''
            },
            keysLocation: (data.keys_location as "Na imobiliária" | "Com o proprietário") || 'Na imobiliária',
            userId: data.user_id || '',
            createdAt: new Date(data.created_at),
            isActive: data.is_active !== false,
            condominiumId: data.condominium_id || undefined
          };
        }
      }
      
      if (foundProperty) {
        console.log('✅ PropertyDetail - Processando imóvel encontrado');
        const filteredProperty = filterPropertyData(foundProperty);
        setProperty(filteredProperty);
        
        // Se o imóvel tem condomínio, buscar as fotos do condomínio
        if (filteredProperty.condominiumId) {
          const condImages = await loadCondominiumData(filteredProperty.condominiumId);
          setCondominiumImages(Array.isArray(condImages) ? condImages : []);
        }
      } else {
        console.warn('❌ PropertyDetail - Imóvel não encontrado em lugar nenhum');
        setError('Imóvel não encontrado');
      }
    } catch (err) {
      console.error('💥 PropertyDetail - Erro ao carregar imóvel:', err);
      setError('Erro ao carregar dados do imóvel');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  // Carregar dados ao montar o componente ou quando o ID mudar
  useEffect(() => {
    console.log('🔄 PropertyDetail - useEffect loadProperty executado, ID:', id);
    loadProperty();
  }, [loadProperty]);

  // Verificar se o imóvel está nos favoritos ao carregar
  useEffect(() => {
    if (property?.listingId) {
      const likedProperties = JSON.parse(localStorage.getItem('likedProperties') || '[]');
      setIsLiked(likedProperties.includes(property.listingId));
    }
  }, [property?.listingId]);

  // SEO meta tags dinâmicas
  useEffect(() => {
    if (property) {
      // Atualizar título da página
      const title = `${property.title} - ${property.location?.neighborhood} - Fox Vendas`;
      document.title = title;
      
      // Atualizar meta description
      const description = `${property.transactionType} - ${property.propertyType} com ${property.bedrooms} dormitórios em ${property.location?.neighborhood}, ${property.location?.city}. ${formatPrice(property.listPrice || property.rentalPrice || 0)}. Veja fotos e detalhes.`;
      
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', description);

      // Canonical URL
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', window.location.href);
    }
    
    return () => {
      // Limpar SEO ao sair da página
      document.title = 'Fox Vendas - Imóveis';
    };
  }, [property]);

  // Funções de formatação
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getTransactionInfo = (transactionType: string) => {
    switch (transactionType) {
      case 'Venda':
        return {
          label: 'À Venda',
          icon: ShoppingCart,
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          borderColor: 'border-green-300'
        };
      case 'Aluguel':
        return {
          label: 'Para Locação',
          icon: Home,
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-800',
          borderColor: 'border-blue-300'
        };
      case 'Venda/Aluguel':
        return {
          label: 'Venda/Locação',
          icon: RefreshCw,
          bgColor: 'bg-purple-100',
          textColor: 'text-purple-800',
          borderColor: 'border-purple-300'
        };
      default:
        return {
          label: transactionType,
          icon: Home,
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          borderColor: 'border-gray-300'
        };
    }
  };

  const handleInterestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!property) return;

    try {
      // Enviar lead para a Edge Function (salva em leads_all e faz atribuição)
      const { error } = await supabase.functions.invoke('receive-lead', {
        body: {
          name: contactForm.name,
          email: contactForm.email || undefined,
          phone: contactForm.phone || undefined,
          message: contactForm.message || `Interesse no imóvel: ${property.title}`,
          source: 'website',
          property_id: property.listingId,
          metadata: {
            property_title: property.title,
            from_page: 'PropertyDetailPage'
          }
        }
      });

      if (error) throw error;

      alert('Obrigado! Seu interesse foi registrado e o responsável receberá uma notificação.');
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      console.error('💥 Erro ao enviar lead:', err);
      alert('Não foi possível enviar seu interesse agora. Tente novamente mais tarde.');
    }
  };

  const openImageModal = () => {
    const allImages = getAllImages();
    console.log('🖼️ Abrindo modal de imagem, index:', currentImageIndex);
    console.log('🖼️ URL da imagem:', allImages[currentImageIndex]?.url);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  const nextImageModal = () => {
    const allImages = getAllImages();
    setCurrentImageIndex((prev) => 
      prev === allImages.length - 1 ? 0 : prev + 1
    );
  };

  const prevImageModal = () => {
    const allImages = getAllImages();
    setCurrentImageIndex((prev) => 
      prev === 0 ? allImages.length - 1 : prev - 1
    );
  };

  const handleLike = () => {
    if (!property) return;
    
    setIsLiked(!isLiked);
    const likedProperties = JSON.parse(localStorage.getItem('likedProperties') || '[]');
    if (!isLiked) {
      likedProperties.push(property.listingId);
      localStorage.setItem('likedProperties', JSON.stringify(likedProperties));
    } else {
      const updatedLikes = likedProperties.filter((id: string) => id !== property.listingId);
      localStorage.setItem('likedProperties', JSON.stringify(updatedLikes));
    }
  };

  const handleShare = async () => {
    if (!property) return;
    
    const url = window.location.href;
    const title = property.title || 'Imóvel';
    const text = `Confira este imóvel: ${title}`;

    try {
      // Tentar usar a Web Share API se disponível (principalmente mobile)
      if (navigator.share) {
        await navigator.share({
          title,
          text,
          url
        });
      } else {
        // Fallback: copiar URL para clipboard
        await navigator.clipboard.writeText(url);
        alert('Link copiado para a área de transferência!');
      }
    } catch (error) {
      // Se falhar, usar fallback de copiar URL
      try {
        await navigator.clipboard.writeText(url);
        alert('Link copiado para a área de transferência!');
      } catch (clipboardError) {
        // Último fallback: prompt manual
        prompt('Copie o link abaixo:', url);
      }
    }
  };

  // Combine images for display
  const allImages = getAllImages();
  const fallbackImage = 'https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=800';

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-700 mb-2">
            Carregando imóvel...
          </h2>
          <p className="text-gray-500">
            Aguarde enquanto buscamos as informações
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !property) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            {error || 'Imóvel não encontrado'}
          </h1>
          <p className="text-gray-600 mb-6">
            O imóvel que você está procurando não foi encontrado ou não está mais disponível.
          </p>
          <div className="space-x-4">
            <button
              onClick={() => navigate('/busca')}
              className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors"
            >
              Voltar à Busca
            </button>
            <button
              onClick={loadProperty}
              className="bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
        {/* Back Button */}
        <button
          onClick={() => {
            // Verificar se há filtros ativos
            const hasActiveFilters = searchFilters.location || 
                                   searchFilters.transactionType || 
                                   searchFilters.category || 
                                   searchFilters.type || 
                                   searchFilters.bedrooms > 0 || 
                                   searchFilters.suites > 0 || 
                                   searchFilters.parkingSpaces > 0 || 
                                   searchFilters.priceMin > 0 || 
                                   searchFilters.priceMax < 10000000;
            
            if (hasActiveFilters) {
              // Se há filtros ativos, voltar à busca preservando-os
              navigate('/busca');
            } else {
              // Se não há filtros, voltar à página inicial ou busca
              navigate('/busca');
            }
          }}
          className="flex items-center space-x-2 text-gray-600 hover:text-orange-500 mb-4 sm:mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="text-sm sm:text-base">Voltar à busca</span>
        </button>

        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Image Gallery */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="relative">
                <div 
                  ref={imageSwipeRef}
                  className="relative cursor-pointer select-none"
                  onClick={openImageModal}
                >
                  <img
                    src={allImages[currentImageIndex]?.url || fallbackImage}
                    alt={property.title || 'Imóvel'}
                    className="w-full h-56 sm:h-64 md:h-80 lg:h-96 object-cover hover:opacity-95 transition-opacity pointer-events-none"
                    draggable={false}
                  />
                </div>
                {allImages.length > 1 && (
                  <>
                    {/* Navigation arrows - hidden on mobile */}
                    <button
                      onClick={prevImage}
                      className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-1.5 sm:p-2 rounded-full hover:bg-opacity-70 transition-opacity hidden sm:block"
                    >
                      <ArrowLeft className="w-4 h-4 sm:w-6 sm:h-6" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-1.5 sm:p-2 rounded-full hover:bg-opacity-70 transition-opacity hidden sm:block"
                    >
                      <ArrowLeft className="w-4 h-4 sm:w-6 sm:h-6 rotate-180" />
                    </button>
                  </>
                )}
                <div className="absolute bottom-2 sm:bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-1 sm:space-x-2">
                  {allImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-colors ${
                        index === currentImageIndex 
                          ? 'bg-white' 
                          : 'bg-white bg-opacity-50'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Thumbnail Gallery */}
              <div className="p-2 sm:p-4">
                {allImages.length > 0 && (
                  <div className="text-xs sm:text-sm text-gray-600 mb-2">
                    {allImages.length} foto{allImages.length > 1 ? 's' : ''} 
                    {condominiumImages.length > 0 && (
                      <span className="ml-2 text-orange-600">
                        (incluindo {condominiumImages.length} do condomínio)
                      </span>
                    )}
                  </div>
                )}
                <div className="flex space-x-1 sm:space-x-2 overflow-x-auto pb-2">
                  {allImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 transition-colors relative ${
                        index === currentImageIndex 
                          ? 'border-orange-500' 
                          : 'border-gray-200'
                      }`}
                    >
                      <img
                        src={image?.url || fallbackImage}
                        alt={`${property.title || 'Imóvel'} - ${image.isCondominium ? 'Condomínio' : 'Imóvel'} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {image.isCondominium && (
                        <div className="absolute bottom-0 left-0 right-0 bg-orange-500 text-white text-xs px-1 py-0.5">
                          Cond.
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Property Details */}
            <div className="bg-white rounded-xl shadow-lg p-3 sm:p-6 mt-4 sm:mt-6">
              <div className="mb-4 sm:mb-6">
                {/* Título e Actions - Desktop */}
                <div className="hidden sm:flex sm:items-start sm:justify-between sm:mb-4">
                  <div className="flex-1 mr-6 min-w-0">
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 leading-tight pr-4 flex-1 min-w-0">
                          {property.title || 'Título não disponível'}
                        </h1>
                        {(() => {
                          const transactionInfo = getTransactionInfo(property.transactionType);
                          const IconComponent = transactionInfo.icon;
                          return (
                            <div className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border whitespace-nowrap ${transactionInfo.bgColor} ${transactionInfo.textColor} ${transactionInfo.borderColor}`}>
                              <IconComponent className="w-3 h-3 mr-1" />
                              {transactionInfo.label}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full font-mono">
                        Código: {property.propertyCode}
                      </span>
                      <span className="text-sm text-gray-600 font-medium">
                        {property.propertyType}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 flex-shrink-0">
                    <button 
                      onClick={handleLike}
                      className={`p-3 rounded-lg transition-colors shadow-sm ${
                        isLiked 
                          ? 'bg-red-100 hover:bg-red-200 border border-red-200' 
                          : 'bg-gray-100 hover:bg-gray-200 border border-gray-200'
                      }`}
                      title={isLiked ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                    >
                      <Heart className={`w-5 h-5 ${
                        isLiked ? 'text-red-600 fill-current' : 'text-gray-600'
                      }`} />
                    </button>
                    <button 
                      onClick={handleShare}
                      className="p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors shadow-sm border border-gray-200"
                      title="Compartilhar imóvel"
                    >
                      <Share2 className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </div>

                {/* Título e Actions - Mobile */}
                <div className="sm:hidden">
                  <div className="mb-3">
                    <h1 className="text-xl font-bold text-gray-800 mb-2">
                      {property.title || 'Título não disponível'}
                    </h1>
                    <div className="flex items-center justify-between">
                      {(() => {
                        const transactionInfo = getTransactionInfo(property.transactionType);
                        const IconComponent = transactionInfo.icon;
                        return (
                          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${transactionInfo.bgColor} ${transactionInfo.textColor} ${transactionInfo.borderColor}`}>
                            <IconComponent className="w-3 h-3 mr-1" />
                            {transactionInfo.label}
                          </div>
                        );
                      })()}
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={handleLike}
                          className={`p-1.5 rounded-lg transition-colors ${
                            isLiked 
                              ? 'bg-red-100 hover:bg-red-200' 
                              : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                          title={isLiked ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                        >
                          <Heart className={`w-4 h-4 ${
                            isLiked ? 'text-red-600 fill-current' : 'text-gray-600'
                          }`} />
                        </button>
                        <button 
                          onClick={handleShare}
                          className="p-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                          title="Compartilhar imóvel"
                        >
                          <Share2 className="w-4 h-4 text-gray-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col space-y-2">
                    <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full font-mono self-start">
                      Código: {property.propertyCode}
                    </span>
                    <span className="text-xs text-gray-600">
                      {property.propertyType}
                    </span>
                  </div>
                </div>

                <div className="flex items-center text-gray-600 mb-4">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
                  <span className="text-sm sm:text-base">
                    {property.location?.address || ''}, {property.location?.neighborhood || ''}, {property.location?.city || ''} - {property.location?.state || ''}
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:flex sm:items-center sm:space-x-6 gap-3 sm:gap-0 text-gray-600 mb-6">
                  <div className="flex items-center">
                    <Bed className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    <span className="text-sm sm:text-base">{property.bedrooms || 0} dorm.</span>
                  </div>
                  <div className="flex items-center">
                    <Bath className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    <span className="text-sm sm:text-base">{property.suites || 0} suítes</span>
                  </div>
                  <div className="flex items-center">
                    <Car className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    <span className="text-sm sm:text-base">{property.garage?.quantity || 0} vagas</span>
                  </div>
                  <div className="flex items-center">
                    <Ruler className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    <span className="text-sm sm:text-base">{property.livingArea || 0}m²</span>
                  </div>
                </div>

                {/* Preços */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                  {property.transactionType === 'Venda' && property.listPrice && (
                    <div className="text-xl sm:text-2xl font-bold text-green-600 mb-2">
                      {formatPrice(property.listPrice)}
                    </div>
                  )}
                  {property.transactionType === 'Aluguel' && property.rentalPrice && (
                    <div className="text-xl sm:text-2xl font-bold text-blue-600 mb-2">
                      {formatPrice(property.rentalPrice)}/mês
                    </div>
                  )}
                  {property.transactionType === 'Venda/Aluguel' && (
                    <div className="space-y-1">
                      {property.listPrice && (
                        <div className="text-lg sm:text-xl font-bold text-green-600">
                          Venda: {formatPrice(property.listPrice)}
                        </div>
                      )}
                      {property.rentalPrice && (
                        <div className="text-lg sm:text-xl font-bold text-blue-600">
                          Aluguel: {formatPrice(property.rentalPrice)}/mês
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 mt-2 text-xs sm:text-sm text-gray-600">
                    {property.yearlyTax && (
                      <span>IPTU: {formatPrice(property.yearlyTax)}/mês</span>
                    )}
                    {property.propertyAdministrationFee && (
                      <span>Condomínio: {formatPrice(property.propertyAdministrationFee)}/mês</span>
                    )}
                  </div>
                </div>

                <div className="border-t pt-4 sm:pt-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3">Descrição</h3>
                  <div 
                    className="text-sm sm:text-base text-gray-600 leading-relaxed whitespace-pre-line"
                    dangerouslySetInnerHTML={{ 
                      __html: (property.description || 'Descrição não disponível.')
                        .replace(/\n/g, '<br />')
                    }}
                  />
                </div>
              </div>

              {/* Additional Features */}
              {property.features && property.features.length > 0 && (
                <div className="border-t pt-4 sm:pt-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3">Características</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
                    {property.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                        <span className="text-xs sm:text-sm text-gray-600">{translateAmenity(feature)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            {/* Contact Form */}
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Tenho interesse</h3>
              <form onSubmit={handleInterestSubmit} className="space-y-3 sm:space-y-4">
                <div>
                  <input
                    type="text"
                    placeholder="Seu nome"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm sm:text-base"
                    required
                  />
                </div>
                <div>
                  <input
                    type="email"
                    placeholder="Seu e-mail"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm sm:text-base"
                    required
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    placeholder="Seu telefone"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm sm:text-base"
                    required
                  />
                </div>
                <div>
                  <textarea
                    placeholder="Mensagem (opcional)"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    rows={3}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none text-sm sm:text-base"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-orange-500 text-white py-2 sm:py-3 rounded-lg hover:bg-orange-600 transition-colors font-medium flex items-center justify-center space-x-2 text-sm sm:text-base"
                >
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Solicitar Contato</span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {isImageModalOpen && (
        <ImageModal
          isOpen={isImageModalOpen}
          imageUrl={allImages[currentImageIndex]?.url || fallbackImage}
          title={`${property.title || 'Imóvel'}${allImages[currentImageIndex]?.isCondominium ? ' - Condomínio' : ''}`}
          currentIndex={currentImageIndex}
          totalImages={allImages.length}
          onClose={closeImageModal}
          onNext={nextImageModal}
          onPrev={prevImageModal}
        />
      )}
    </div>
  );
};

export default PropertyDetailPage;